/**
 * Help Center Page — Public
 * Bilingual (EN/PT-BR) help and support page for charstan.com
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  HelpCircle, BookOpen, Mail, MessageCircle, FileText, Video,
  ArrowLeft, ChevronRight, Shield, Search, Clock, Users, Zap
} from 'lucide-react';
import PublicLanguageSwitcher from '../components/PublicLanguageSwitcher';

const HelpCenterPage = () => {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt-BR';

  const content = isPt ? {
    title: 'Central de Ajuda',
    subtitle: 'Recursos, guias e suporte para aproveitar ao maximo a Plataforma de Inteligência CHARSTAN',
    categories: [
      {
        key: 'getting_started',
        icon: Zap,
        title: 'Primeiros Passos',
        desc: 'Aprenda o básico e configure sua organização',
        links: [
          'Guia de integracao da plataforma',
          'Configurar funções e permissoes',
          'Criar seu primeiro documento',
          'Navegar pelo painel',
        ],
      },
      {
        key: 'qms_workflows',
        icon: FileText,
        title: 'Fluxos de Trabalho QMS',
        desc: 'Guias detalhados para cada modulo QMS',
        links: [
          'Controle de documentos e aprovacoes',
          'Gerenciamento de CAPAs',
          'Planejamento e execucao de auditorias',
          'Rastreamento de desvios',
          'Gerenciamento de registros de treinamento',
        ],
      },
      {
        key: 'ai_features',
        icon: Search,
        title: 'Recursos de IA',
        desc: 'Como usar as ferramentas de inteligência da CHARSTAN',
        links: [
          'Assistente de IA — como funciona',
          'Análise de lacunas automatizada',
          'Recomendacoes inteligentes de documentos',
          'Classificacao de risco preditiva',
        ],
      },
      {
        key: 'compliance',
        icon: Shield,
        title: 'Conformidade e Normas',
        desc: 'Entenda como a CHARSTAN apoia a conformidade regulatória',
        links: [
          'Alinhamento com ISO 13485',
          'Conformidade com FDA 21 CFR Part 11',
          'Preparação para EU GMP Annex 11',
          'Trilhas de auditoria e integridade de dados',
        ],
      },
      {
        key: 'account',
        icon: Users,
        title: 'Conta e Organização',
        desc: 'Gerencie sua conta, equipe e configurações',
        links: [
          'Gerenciar membros da equipe',
          'Configurar SSO e autenticacao',
          'Política de retenção de dados',
          'Exportar dados da sua organização',
        ],
      },
      {
        key: 'troubleshooting',
        icon: HelpCircle,
        title: 'Resolucao de Problemas',
        desc: 'Solucoes para problemas comuns',
        links: [
          'Problemas de login e sessão',
          'Erros de upload de documentos',
          'Problemas de sincronizacao de dados',
          'Compatibilidade de navegador',
        ],
      },
    ],
    contact: {
      title: 'Precisa de mais ajuda?',
      desc: 'Nossa equipe de suporte esta disponivel para ajudar com questoes tecnicas e de conformidade.',
      email: 'support@charstan.com',
      hours: 'Horario de suporte: Seg-Sex, 08:00 - 18:00 (GMT)',
      response: 'Tempo de resposta tipico: menos de 4 horas uteis',
    },
  } : {
    title: 'Help Center',
    subtitle: 'Resources, guides, and support to get the most out of the CHARSTAN Intelligence Platform',
    categories: [
      {
        key: 'getting_started',
        icon: Zap,
        title: 'Getting Started',
        desc: 'Learn the basics and set up your organization',
        links: [
          'Platform onboarding guide',
          'Setting up roles and permissions',
          'Creating your first document',
          'Navigating the dashboard',
        ],
      },
      {
        key: 'qms_workflows',
        icon: FileText,
        title: 'QMS Workflows',
        desc: 'Detailed guides for each QMS module',
        links: [
          'Document control and approvals',
          'CAPA management',
          'Audit planning and execution',
          'Deviation tracking',
          'Training record management',
        ],
      },
      {
        key: 'ai_features',
        icon: Search,
        title: 'AI Features',
        desc: 'How to use CHARSTAN intelligence tools',
        links: [
          'AI Assistant — how it works',
          'Automated gap analysis',
          'Smart document recommendations',
          'Predictive risk scoring',
        ],
      },
      {
        key: 'compliance',
        icon: Shield,
        title: 'Compliance & Standards',
        desc: 'Understand how CHARSTAN supports regulatory compliance',
        links: [
          'ISO 13485 alignment',
          'FDA 21 CFR Part 11 compliance',
          'EU GMP Annex 11 readiness',
          'Audit trails and data integrity',
        ],
      },
      {
        key: 'account',
        icon: Users,
        title: 'Account & Organization',
        desc: 'Manage your account, team, and settings',
        links: [
          'Managing team members',
          'Configuring SSO and authentication',
          'Data retention policy',
          'Exporting your organization data',
        ],
      },
      {
        key: 'troubleshooting',
        icon: HelpCircle,
        title: 'Troubleshooting',
        desc: 'Solutions for common issues',
        links: [
          'Login and session issues',
          'Document upload errors',
          'Data sync problems',
          'Browser compatibility',
        ],
      },
    ],
    contact: {
      title: 'Need more help?',
      desc: 'Our support team is available to help with technical and compliance questions.',
      email: 'support@charstan.com',
      hours: 'Support hours: Mon-Fri, 08:00 - 18:00 (GMT)',
      response: 'Typical response time: under 4 business hours',
    },
  };

  const c = content;

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white" data-testid="help-page">
      {/* Header */}
      <header className="border-b border-[rgba(255,255,255,0.06)] bg-[#0A0E17]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#36A9E1] transition-colors" data-testid="help-back-home">
            <ArrowLeft size={18} />
            <span className="font-semibold text-lg tracking-tight">CHARSTAN</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/trust" className="text-sm text-[#8B95A5] hover:text-white transition-colors">
              {isPt ? 'Confiança' : 'Trust'}
            </Link>
            <Link to="/privacy" className="text-sm text-[#8B95A5] hover:text-white transition-colors">
              {isPt ? 'Privacidade' : 'Privacy'}
            </Link>
            <PublicLanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-[rgba(54,169,225,0.1)]">
            <BookOpen className="text-[#36A9E1]" size={28} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" data-testid="help-title">{c.title}</h1>
        </div>
        <p className="text-[#A0AEC0] text-lg max-w-2xl">{c.subtitle}</p>
      </section>

      {/* Categories Grid */}
      <main className="max-w-5xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {c.categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.key}
                className="bg-[#111827] border border-[rgba(255,255,255,0.06)] rounded-xl p-6 hover:border-[rgba(54,169,225,0.2)] transition-colors"
                data-testid={`help-category-${cat.key}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-[rgba(54,169,225,0.1)] shrink-0">
                    <Icon className="text-[#36A9E1]" size={20} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">{cat.title}</h2>
                    <p className="text-[#8B95A5] text-sm mt-1">{cat.desc}</p>
                  </div>
                </div>
                <ul className="space-y-2 ml-2">
                  {cat.links.map((link, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#A0AEC0] text-sm hover:text-[#36A9E1] transition-colors cursor-pointer">
                      <ChevronRight size={14} className="text-[#36A9E1] shrink-0" />
                      <span>{link}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </main>

      {/* Contact Support */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="bg-[#111827] border border-[rgba(54,169,225,0.15)] rounded-xl p-8" data-testid="help-contact">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-[rgba(54,169,225,0.1)] shrink-0">
              <MessageCircle className="text-[#36A9E1]" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{c.contact.title}</h2>
              <p className="text-[#A0AEC0] mt-2">{c.contact.desc}</p>
              <div className="mt-4 space-y-2">
                <a
                  href={`mailto:${c.contact.email}`}
                  className="inline-flex items-center gap-2 text-[#36A9E1] hover:text-[#38BDF8] transition-colors font-medium"
                  data-testid="help-contact-email"
                >
                  <Mail size={16} />
                  {c.contact.email}
                </a>
                <p className="text-[#8B95A5] text-sm flex items-center gap-2">
                  <Clock size={14} />
                  {c.contact.hours}
                </p>
                <p className="text-[#8B95A5] text-sm flex items-center gap-2">
                  <Zap size={14} />
                  {c.contact.response}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[rgba(255,255,255,0.06)] bg-[#0A0E17] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-[#4A5568] text-sm">&copy; 2026 CHARSTAN. {isPt ? 'Todos os direitos reservados.' : 'All rights reserved.'}</span>
          <div className="flex items-center gap-6">
            <Link to="/trust" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Confiança' : 'Trust'}</Link>
            <Link to="/privacy" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Privacidade' : 'Privacy'}</Link>
            <Link to="/governance" className="text-[#8B95A5] hover:text-white text-sm transition-colors">{isPt ? 'Governança' : 'Governance'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
